import { type ReactNode, type SyntheticEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Board,
  boardColor,
  displayColumnName,
  useBoardDetail,
  useCreateCalendarCard,
} from '@/entities/board';
import type { QuickCreateDefaults } from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import {
  type DeadlineDraft,
  type DeadlineErrorCode,
  draftDuration,
  draftToPayload,
  validateDraft,
  validateEventDraft,
} from '@/shared/lib/deadline-draft';
import {
  defaultDraft,
  draftToInput,
  RecurrenceFields,
  type RecurrenceDraft,
  type RecurrenceError,
  validateRecurrence,
} from '@/entities/recurrence';
import { Button, Combobox, Dialog, OverdueToggle, RangeGroups } from '@/shared/ui';

import styles from './CreateEventDialog.module.css';
import type { CreateDraft } from './model';

interface Props {
  /** Boards a task can be created on (a board calendar passes just its own, fixed). */
  boards: readonly Board[];
  /** Workspace names by id: told apart in the board list (the global calendar passes them). */
  workspaceNames?: ReadonlyMap<string, string>;
  /** Workspace calendar: the user picks the board; a board calendar has it fixed. */
  chooseBoard: boolean;
  draft: CreateDraft;
  timezone: string | null;
  /** Namespaces the "last used" memory (workspace id or board id). */
  memoryKey: string;
  /** The user's configured quick-create destination (workspace calendar only). */
  quickDefaults?: QuickCreateDefaults | null;
  /** The dialog's title when it is not the plain "New event" (the text quick-creation). */
  heading?: string;
  /** Extra content above the form (the text quick-creation's input). */
  intro?: ReactNode;
  /** Values read from a sentence: applied when `id` changes, editable like everything else. */
  prefill?: EventPrefill | null;
  /** The destination board must be chosen explicitly (no default is picked for the person). */
  requireBoardChoice?: boolean;
  onClose: () => void;
}

/** Fields of the form filled from outside (a parsed sentence). */
export interface EventPrefill {
  /** A new id = a new reading: the form takes these values (and drops the previous edits). */
  id: number;
  title: string;
  form: DeadlineDraft;
  allDay: boolean;
  repeat: RecurrenceDraft | null;
  /** A board named in the sentence: chosen when the dialog lets the person choose one. */
  boardId?: string;
}

const remember = {
  read(key: string): string | null {
    try {
      return window.localStorage.getItem(`zadok.calendar.${key}`);
    } catch {
      return null;
    }
  },
  write(key: string, value: string): void {
    try {
      window.localStorage.setItem(`zadok.calendar.${key}`, value);
    } catch {
      // Private mode: the preference just is not kept.
    }
  },
};

/**
 * Light "new event" popup of the calendar. Start date + time and end date + time are visible at
 * once (no hidden section) and both are required: the end must be later than the start. "All
 * day" turns the times off; "only a due date" makes a task instead of an event. The card goes to
 * the board's system Calendar column unless another column is chosen. It creates a regular card
 * with its deadline; the full card dialog is for everything else.
 */
export function CreateEventDialog({
  boards,
  workspaceNames,
  chooseBoard,
  draft,
  timezone,
  memoryKey,
  quickDefaults = null,
  heading,
  intro,
  prefill = null,
  requireBoardChoice = false,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const id = useId();
  const create = useCreateCalendarCard();
  const titleInput = useRef<HTMLInputElement>(null);
  // The dialog focuses its close button when it opens; the title is what the user types next.
  useEffect(() => {
    const timer = window.setTimeout(() => titleInput.current?.focus());
    return () => {
      window.clearTimeout(timer);
    };
  }, []);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [boardId, setBoardId] = useState(() => {
    // Configured default board > the board used last > the first one.
    const configured = chooseBoard
      ? boards.find((board) => board.id === quickDefaults?.default_board_id)
      : undefined;
    if (requireBoardChoice) return '';
    const last = remember.read(`board.${memoryKey}`);
    return configured?.id ?? boards.find((board) => board.id === last)?.id ?? boards[0]?.id ?? '';
  });
  const [columnChoice, setColumnChoice] = useState<string | null>(null);
  const [form, setForm] = useState<DeadlineDraft>({ hasStart: true, ...draft });
  // A click on a month cell / the all-day row has no time: the event starts as an all-day one.
  const [allDay, setAllDay] = useState(draft.startTime === '' && draft.endTime === '');
  const [errors, setErrors] = useState<DeadlineErrorCode[]>([]);
  // Optional recurrence: the event becomes the source of a series (its start is the series start).
  const [repeat, setRepeat] = useState<RecurrenceDraft | null>(null);
  const [repeatErrors, setRepeatErrors] = useState<RecurrenceError[]>([]);
  const detail = useBoardDetail(boardId, boardId !== '');
  const columns = detail.data?.columns ?? [];
  const board = boards.find((candidate) => candidate.id === boardId);
  // Explicit choice > configured default column > the board's default column > the one used
  // last > the first active column (a stale default silently falls through).
  const configuredColumn =
    chooseBoard && quickDefaults?.default_board_id === boardId
      ? quickDefaults.default_column_id
      : null;
  const columnId =
    columns.find((column) => column.id === columnChoice)?.id ??
    // Events live in the board's system Calendar column unless the user picks another one.
    columns.find((column) => column.system_kind === 'calendar')?.id ??
    columns.find((column) => column.id === configuredColumn)?.id ??
    columns.find((column) => column.id === board?.default_column_id)?.id ??
    columns[0]?.id ??
    '';

  // A new reading of a sentence replaces the form's values (adjusted while rendering: no effect).
  const [appliedPrefill, setAppliedPrefill] = useState<number | null>(null);
  if (prefill && prefill.id !== appliedPrefill) {
    setAppliedPrefill(prefill.id);
    setTitle(prefill.title);
    setTitleError(false);
    setForm(prefill.form);
    setAllDay(prefill.allDay);
    setRepeat(prefill.repeat);
    if (prefill.boardId && chooseBoard && boards.some((b) => b.id === prefill.boardId)) {
      setBoardId(prefill.boardId);
      setColumnChoice(null);
    }
    setErrors([]);
    setRepeatErrors([]);
  }

  const set = (patch: Partial<DeadlineDraft>) => {
    setForm((current) => ({ ...current, ...patch }));
    setErrors([]);
  };
  const has = (...codes: DeadlineErrorCode[]) => codes.some((code) => errors.includes(code));

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    const found = form.hasStart ? validateEventDraft(form, allDay) : validateDraft(form);
    setTitleError(trimmed === '');
    setErrors(found);
    const repeatFound = repeat ? validateRecurrence(repeat) : [];
    setRepeatErrors(repeatFound);
    if (trimmed === '' || found.length > 0 || repeatFound.length > 0 || !columnId) return;
    create.mutate(
      {
        columnId,
        title: trimmed,
        deadline: draftToPayload(
          allDay && form.hasStart ? { ...form, startTime: '', endTime: '' } : form,
        ),
        ...(repeat && { recurrence: draftToInput(repeat, timezone) }),
      },
      {
        onSuccess: () => {
          remember.write(`board.${memoryKey}`, boardId);
          onClose();
        },
      },
    );
  };

  const errorId = `${id}-errors`;
  return (
    <Dialog
      title={heading ?? t(form.hasStart ? 'calendar.newEvent' : 'calendar.newTask')}
      onClose={onClose}
    >
      {intro}
      <form className={styles.form} noValidate onSubmit={submit} data-testid="calendar-create">
        <div className={styles.field}>
          <label htmlFor={`${id}-title`} className={styles.label}>
            {t('cards.title')}
          </label>
          <input
            id={`${id}-title`}
            ref={titleInput}
            className={styles.input}
            value={title}
            maxLength={500}
            autoComplete="off"
            aria-invalid={titleError}
            onChange={(event) => {
              setTitle(event.target.value);
              setTitleError(false);
            }}
          />
          {titleError && (
            <span role="alert" className={styles.error}>
              {t('validation.REQUIRED')}
            </span>
          )}
        </div>

        <div className={styles.destination}>
          {chooseBoard ? (
            <div className={styles.field}>
              <label htmlFor={`${id}-board`} className={styles.label}>
                {t('calendar.board')}
              </label>
              <Combobox
                id={`${id}-board`}
                label={t('calendar.board')}
                placeholder={t('calendar.text.chooseBoard')}
                value={boardId || null}
                options={boards.map((option) => {
                  const workspaceName = option.workspace_id
                    ? workspaceNames?.get(option.workspace_id)
                    : undefined;
                  return {
                    value: option.id,
                    label: option.name,
                    ...(workspaceName && { description: workspaceName }),
                    leading: (
                      <span
                        className={styles.dot}
                        style={{ background: boardColor(option) }}
                        aria-hidden
                      />
                    ),
                  };
                })}
                onChange={(next) => {
                  setBoardId(next);
                  setColumnChoice(null);
                }}
              />
            </div>
          ) : (
            board && (
              <p className={styles.fixedBoard}>
                <span
                  className={styles.dot}
                  style={{ background: boardColor(board) }}
                  aria-hidden
                />
                {board.name}
              </p>
            )
          )}
          <div className={styles.field}>
            <label htmlFor={`${id}-column`} className={styles.label}>
              {t('calendar.column')}
            </label>
            <Combobox
              id={`${id}-column`}
              label={t('calendar.column')}
              value={columnId}
              disabled={columns.length === 0}
              options={columns.map((column) => ({
                value: column.id,
                label: displayColumnName(column, t('columns.calendar')),
              }))}
              onChange={setColumnChoice}
            />
            {detail.isSuccess && columns.length === 0 && (
              <span role="alert" className={styles.error}>
                {t('calendar.noColumns')}
              </span>
            )}
          </div>
        </div>

        <RangeGroups
          value={form}
          showStart={form.hasStart}
          showTimes={!allDay || !form.hasStart}
          timeOptional={!form.hasStart}
          describedBy={errorId}
          duration={
            form.hasStart && errors.length === 0
              ? draftDuration(allDay ? { ...form, startTime: '', endTime: '' } : form)
              : null
          }
          invalid={{
            startDate: has('startRequired', 'startDateInvalid', 'endBeforeStart'),
            startTime: has('startTimeInvalid', 'startTimeRequired', 'endNotAfterStart'),
            endDate: has('endRequired', 'endDateInvalid', 'endBeforeStart'),
            endTime: has('endTimeInvalid', 'endTimeRequired', 'endNotAfterStart'),
          }}
          onChange={set}
        />
        {form.hasStart && (
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={allDay}
              onChange={(event) => {
                setAllDay(event.target.checked);
                set(
                  event.target.checked
                    ? { startTime: '', endTime: '' }
                    : {
                        startTime: form.startTime || '09:00',
                        endTime:
                          form.endTime || (form.endDate === form.startDate ? '10:00' : '09:00'),
                      },
                );
              }}
            />
            {t('calendar.allDayEvent')}
          </label>
        )}
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={!form.hasStart}
            onChange={(event) => {
              set(
                event.target.checked
                  ? { hasStart: false, startDate: '', startTime: '' }
                  : { hasStart: true, startDate: form.endDate, startTime: '' },
              );
            }}
          />
          {t('calendar.deadlineOnly')}
        </label>

        <label className={styles.check}>
          <input
            type="checkbox"
            checked={repeat !== null}
            onChange={(event) => {
              setRepeat(event.target.checked ? defaultDraft(form.startDate || form.endDate) : null);
              setRepeatErrors([]);
            }}
          />
          {t('recurrence.advancedRepeat')}
        </label>
        {repeat && (
          <RecurrenceFields
            draft={repeat}
            onChange={(next) => {
              setRepeat(next);
              setRepeatErrors([]);
            }}
            errors={repeatErrors}
            timezone={timezone}
            locale={i18n.language}
          />
        )}

        {errors.length > 0 && (
          <p id={errorId} role="alert" className={styles.error}>
            {errors.map((code) => t(`deadline.errors.${code}`)).join(' ')}
          </p>
        )}
        <div className={styles.footer}>
          <p className={styles.hint}>
            {[
              form.hasStart || form.endTime ? '' : t('deadline.dueEndOfDay'),
              form.hasStart ? t('calendar.eventDefaultsHint') : '',
              timezone ? t('deadline.zoneHint', { zone: timezone }) : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {form.hasStart && (
            <OverdueToggle
              value={form.overdueEnabled === true}
              onChange={(overdueEnabled) => {
                set({ overdueEnabled });
              }}
            />
          )}
        </div>
        {create.isError && (
          <p role="alert" className={styles.error}>
            {getErrorMessage(create.error)}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={create.isPending || !columnId}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
